import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';
import { useState } from 'react';
import { useEnvVariables } from '../../../../env-variables-manager';
import {
  getPacingGuideFileName,
  getPersonalizedPracticeFileName,
} from '../get-file-name';

const Container = styled.div`
  margin-top: 24px;
  padding: 16px;
  border: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-accent'].value};
  border-radius: 8px;
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
`;

const ErrorItem = styled.li`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const RetryLink = styled.a<{ disabled?: boolean }>`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-accent'].value};
  text-decoration: underline;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-left: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    text-decoration: ${({ disabled }) => (disabled ? 'underline' : 'none')};
  }
`;

const SuccessText = styled.span`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-success'].value};
  margin-left: 8px;
`;

type FailedDownload = {
  stretch: string;
  module?: string;
  params: {
    courseId: string;
    documentType: string;
    stretch: string;
    module?: string;
    hideStandards?: string;
  };
};

type Props = {
  failedDownloads: FailedDownload[];
};

type DownloadState = {
  [key: string]: 'idle' | 'downloading' | 'success';
};

export function FailedDownloads({ failedDownloads }: Props): JSX.Element {
  const [downloadStates, setDownloadStates] = useState<DownloadState>({});
  const { APOLLO_SERVER } = useEnvVariables();
  const baseUrl = APOLLO_SERVER.endsWith('/')
    ? APOLLO_SERVER.slice(0, -1)
    : APOLLO_SERVER;

  const getDownloadKey = (download: FailedDownload): string => {
    return `${download.stretch}-${download.module || ''}`;
  };

  const handleRetry = async (
    download: FailedDownload,
    params: FailedDownload['params']
  ): Promise<void> => {
    const key = getDownloadKey(download);
    setDownloadStates((prev) => ({ ...prev, [key]: 'downloading' }));

    try {
      const response = await fetch(
        `${baseUrl}/api/download-us-documents?${new URLSearchParams(params)}`
      );

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const fileName = params.module
        ? getPersonalizedPracticeFileName({
            courseId: params.courseId,
            stretch: params.stretch,
            module: params.module,
          })
        : getPacingGuideFileName({
            courseId: params.courseId,
            stretch: params.stretch,
          });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadStates((prev) => ({ ...prev, [key]: 'success' }));
    } catch (error) {
      console.error('Error retrying download:', error);
      setDownloadStates((prev) => ({ ...prev, [key]: 'idle' }));
    }
  };

  const renderDownloadStatus = (download: FailedDownload): JSX.Element => {
    const key = getDownloadKey(download);
    const state = downloadStates[key] || 'idle';

    switch (state) {
      case 'downloading':
        return <RetryLink disabled>Downloading...</RetryLink>;
      case 'success':
        return <SuccessText>Downloaded</SuccessText>;
      default:
        return (
          <RetryLink onClick={() => handleRetry(download, download.params)}>
            Download again
          </RetryLink>
        );
    }
  };

  return (
    <Container>
      <Typography.Body1>
        The following files were not downloaded correctly:
      </Typography.Body1>
      <ErrorList>
        {failedDownloads.map((download, index) => (
          <ErrorItem key={index}>
            <Typography.Body2>
              Stretch {download.stretch}
              {download.module ? `, Module ${download.module}` : ''}
              {renderDownloadStatus(download)}
            </Typography.Body2>
          </ErrorItem>
        ))}
      </ErrorList>
    </Container>
  );
}
